export interface Article {
  id: string;
  slug: string;
  title: string;
  category: string;
  categorySlug: string;
  excerpt: string;
  content: string;
  imageSeq: string;
  date: string;
  readTime: string;
  author: string;
  tags: string[];
  featured: boolean;
}

export interface Category {
  name: string;
  slug: string;
  description: string;
  icon: string;
  imageSeq: string;
}

export interface Testimonial {
  id: string;
  name: string;
  role: string;
  content: string;
  avatar: string;
}

export const categories: Category[] = [
  {
    name: '婚后角色转变',
    slug: 'role-transition',
    description: '从女儿到妻子，从容完成身份转换，找到属于你的新位置',
    icon: 'ri-user-heart-line',
    imageSeq: 'category-role-transition',
  },
  {
    name: '夫妻相处技巧',
    slug: 'couple-skills',
    description: '学会倾听与表达，让爱在日常相处中持续升温',
    icon: 'ri-heart-2-line',
    imageSeq: 'category-couple-skills',
  },
  {
    name: '婚姻经营秘诀',
    slug: 'marriage-secrets',
    description: '掌握幸福婚姻的核心密码，经营一生的浪漫约定',
    icon: 'ri-key-2-line',
    imageSeq: 'category-marriage-secrets',
  },
  {
    name: '新婚适应期攻略',
    slug: 'newlywed-guide',
    description: '平稳度过磨合期，让新婚生活甜蜜又从容',
    icon: 'ri-home-heart-line',
    imageSeq: 'category-newlywed-guide',
  },
  {
    name: '家庭经营之道',
    slug: 'family-management',
    description: '从柴米油盐到诗和远方，打造温馨有序的家庭生活',
    icon: 'ri-building-2-line',
    imageSeq: 'category-family-management',
  },
  {
    name: '自我成长',
    slug: 'self-growth',
    description: '在婚姻中保持独立与光芒，做最好的自己',
    icon: 'ri-sun-line',
    imageSeq: 'category-self-growth',
  },
];

export const articles: Article[] = [
  {
    id: '1',
    slug: 'how-to-adapt-to-new-role-after-marriage',
    title: '婚姻第一课：如何顺利完成婚后角色转变',
    category: '婚后角色转变',
    categorySlug: 'role-transition',
    excerpt: '从女友到妻子，这个身份的转变不仅仅是称呼的改变，更是一整套生活方式的重新定义。本文带你一步步理解并拥抱这个美好的新角色。',
    content: `<p>婚礼的钟声还在耳边回响，蜜月的甜蜜尚未褪去，你已经正式成为一名"人妻"。这个称呼，承载着太多期待、责任和未知。很多新婚女性都会在婚后最初几个月感到一种微妙的迷茫——我还是原来的我，但似乎一切都不一样了。</p>
<h2>理解角色转变的本质</h2>
<p>从女友到妻子的转变，并非简单的身份标签更换。它是社会角色、家庭角色和情感角色的三重叠加。你在父母面前依然是那个需要被呵护的女儿，在丈夫面前却是需要共同承担生活的伴侣。这种多重身份的切换，是新婚女性面临的第一道功课。</p>
<p>心理学研究表明，婚后6至12个月是新婚女性角色适应最关键的时期。在这个阶段，女性往往会经历从"我"到"我们"的思维模式转变。这个转变不是失去自我，而是扩展自我——你依然是你，但你的世界变得更大了。</p>
<h2>调整心态的三个关键</h2>
<p><strong>第一，接纳不确定性。</strong>婚姻不是童话故事的结局，而是真实生活的开始。接受婚姻中会有磨合、有不确定、有需要学习的地方，这种接纳本身就是一种力量。</p>
<p><strong>第二，建立新的自我认同。</strong>妻子的身份不是对过去自我的否定，而是一种丰富。你可以同时是优秀的职场人、贴心的妻子、孝顺的女儿，这些身份并不冲突。</p>
<p><strong>第三，保持开放的沟通。</strong>很多角色适应的困难源于不沟通的猜测。你觉得丈夫希望你成为一个"完美的妻子"，而他可能只是希望你开心。直接、温和地表达你的感受和困惑，是化解角色焦虑最有效的方式。</p>
<h2>日常生活中的角色实践</h2>
<p>角色转变最终落在每一天的细节里。早晨醒来时的第一个微笑，下班回家后的一杯温水，周末一起做的第一顿早餐——这些看似微不足道的日常，才是角色真正的土壤。</p>
<p>给自己一些时间，也给婚姻一些时间。没有人天生就知道如何做一个完美的妻子，正如没有人天生就知道如何做一个完美的丈夫。婚姻是一门共同修习的课程，你们是彼此最好的老师和同学。</p>`,
    imageSeq: 'article-role-1',
    date: '2026-07-28',
    readTime: '8分钟',
    author: '心安',
    tags: ['角色转变', '自我认知', '新婚心理'],
    featured: true,
  },
  {
    id: '2',
    slug: 'five-communication-skills-for-newlyweds',
    title: '新婚夫妻必学的五个沟通技巧，让爱不再有误会',
    category: '夫妻相处技巧',
    categorySlug: 'couple-skills',
    excerpt: '沟通是婚姻的氧气。掌握这五个简单却强大的沟通技巧，你将发现夫妻之间的每一次对话都能成为增进感情的契机。',
    content: `<p>有人说，婚姻中最远的距离不是天各一方，而是两个人坐在同一张沙发上，却听不懂彼此在说什么。沟通，是贯穿婚姻全程的核心能力。尤其对于新婚夫妻来说，建立良好的沟通模式，等于为未来的几十年打下坚实的情感地基。</p>
<h2>技巧一：学会"我"的表达</h2>
<p>试着把"你总是..."、"你为什么..."换成"我觉得..."、"我需要..."。同样一件事，"你怎么又忘了倒垃圾"和"我觉得垃圾需要倒了，你能帮忙吗"带来的效果天差地别。前者是指责，后者是请求。用"我"开头的句子表达你的感受和需求，而不是用"你"开头的句子去评判对方。</p>
<h2>技巧二：先倾听，再回应</h2>
<p>当丈夫向你倾诉工作上的烦恼时，他需要的往往不是解决方案，而是一个真正倾听的人。不要急着给出建议，先试着复述他的感受："听起来你今天真的很累"，"这件事确实让人很沮丧"。被理解的感觉，比任何建议都更有治愈力。</p>
<h2>技巧三：选择合适的时机</h2>
<p>刚下班疲惫不堪时不适合讨论家庭财务，深夜困倦时不适合探讨感情问题。学会判断"现在是不是沟通的好时机"，并且敢于说"这件事很重要，我们找个合适的时间好好聊聊吧"。</p>
<h2>技巧四：非语言沟通的力量</h2>
<p>一个拥抱、一次牵手、一个理解的眼神，有时候胜过千言万语。身体接触是夫妻之间最直接的情感连接方式。每天至少有一次认真的身体接触——不是匆忙的例行公事，而是带着温度的拥抱。</p>
<h2>技巧五：建立定期沟通的仪式</h2>
<p>每周找一个固定的时间，放下手机，关掉电视，面对面坐着聊聊这一周的感受。可以是周六早晨的咖啡时间，也可以是周日傍晚的散步时间。这个仪式会成为你们婚姻中最珍贵的传统。</p>`,
    imageSeq: 'article-couple-1',
    date: '2026-07-25',
    readTime: '10分钟',
    author: '心安',
    tags: ['夫妻沟通', '情感交流', '新婚技巧'],
    featured: true,
  },
  {
    id: '3',
    slug: 'three-pillars-of-happy-marriage',
    title: '幸福婚姻的三大支柱：尊重、信任与共同成长',
    category: '婚姻经营秘诀',
    categorySlug: 'marriage-secrets',
    excerpt: '真正持久的婚姻，不是找到了完美的人，而是两个人愿意一起变得更好。探索幸福婚姻最核心的三个要素。',
    content: `<p>如果你问一百对结婚超过三十年的夫妻，他们幸福的秘诀是什么，你大概会得到一百个不同的答案。但如果你仔细听，会发现这些答案背后都隐藏着三个共同的元素：尊重、信任与共同成长。</p>
<h2>尊重：婚姻的第一原则</h2>
<p>尊重不是相敬如宾的表面客气，而是发自内心地认可对方是一个独立的、有价值的个体。这意味着尊重他的职业选择，即使你不太理解他为什么那么热爱那份工作；尊重他的爱好，即使你看不懂足球比赛的规则；尊重他的独处需求，即使你觉得夫妻就应该时时刻刻在一起。</p>
<p>同样地，你也需要被尊重。勇敢地表达你的界限，温柔而坚定地维护你的空间。一个不懂得尊重你的人，不可能真正爱你。</p>
<h2>信任：婚姻的基石</h2>
<p>信任就像空气，它在那里的时候你感觉不到，一旦缺失你就会窒息。信任不仅仅是相信对方不会背叛你，更是相信对方在关键时刻会站在你这边，相信你们的共同目标比个人利益更重要。</p>
<p>建立信任需要时间，摧毁信任只需要一瞬间。在日常小事中积累信任——说到做到，坦诚相待，不隐瞒、不欺骗。当信任的账户余额充足时，偶尔的摩擦和误会才不会造成致命伤害。</p>
<h2>共同成长：婚姻的生命力</h2>
<p>最美好的婚姻状态是什么？是两个人朝着同一个方向成长。你读一本书，分享给他听；他学到一个新观点，和你一起讨论。你们可以是彼此最好的朋友、最严格的老师、最忠实的粉丝。</p>
<p>一起制定年度目标，一起学习新技能，一起去没去过的地方旅行。当你们都在成长时，你们的婚姻也在成长。停滞不前的婚姻，最终会被时间磨损。</p>`,
    imageSeq: 'article-marriage-1',
    date: '2026-07-22',
    readTime: '9分钟',
    author: '心安',
    tags: ['婚姻经营', '幸福秘诀', '夫妻关系'],
    featured: true,
  },
  {
    id: '4',
    slug: 'newlywed-first-month-survival-guide',
    title: '新婚第一个月生存指南：从手忙脚乱到从容不迫',
    category: '新婚适应期攻略',
    categorySlug: 'newlywed-guide',
    excerpt: '蜜月归来，真正的新婚生活才刚刚开始。面对柴米油盐的日常，这份实用指南帮你平稳度过最关键的适应期。',
    content: `<p>蜜月旅行的行李还没收拾完，你已经站在了真实婚姻生活的起跑线上。新婚第一个月，是甜蜜与混乱并存的三十天。你们要从恋爱的"展示模式"切换到婚姻的"日常模式"，这个切换过程可能会比你想象的更具挑战性。</p>
<h2>生活节奏的重新校准</h2>
<p>恋爱时，约会时间是被精心安排的。结婚后，你们要在同一个空间里度过所有的清晨和夜晚。他的早起习惯可能打扰你的懒觉，你的夜间阅读可能影响他的睡眠。这些都是正常的新婚磨合，不要把它们解读为"我们是不是不合适"。</p>
<p>建立一个双方都能接受的生活节奏表。谁先起床、谁做早餐、晚上几点熄灯——这些看似琐碎的小事，恰恰是新婚生活最需要协调的部分。</p>
<h2>财务管理的初次对话</h2>
<p>新婚第一个月，是讨论财务安排的最佳时机。你们需要一个共同的账户吗？日常开销怎么分担？大额支出需要双方同意吗？这些问题的答案没有标准模板，关键在于坦诚沟通，找到最适合你们的方案。</p>
<p>建议从建立一个小额的共同生活基金开始，用于日常买菜、交水电费等。随着信任和默契的增加，再逐步扩大共同财务管理的范围。</p>
<h2>与双方家庭的边界建立</h2>
<p>新婚第一个月，双方父母可能会频繁上门或来电。他们的关心是出于爱，但过度的介入可能会让新婚夫妻感到压力。和丈夫一起，温和但坚定地和双方家庭建立合理的边界。告诉父母你们需要一些独立的空间来适应新生活，同时保持定期的联系和探望。</p>
<h2>给彼此一些"不完美"的空间</h2>
<p>新婚第一个月，你可能会发现丈夫身上一些之前没注意到的"小毛病"。他可能会把袜子扔在地上，可能会忘记关卫生间的灯。在你想要纠正这些之前，先问问自己：这件事真的那么重要吗？学会区分"原则性问题"和"可以接受的差异"，是婚姻中最省力的智慧。</p>`,
    imageSeq: 'article-newlywed-1',
    date: '2026-07-20',
    readTime: '10分钟',
    author: '心安',
    tags: ['新婚适应', '生活指南', '磨合期'],
    featured: false,
  },
  {
    id: '5',
    slug: 'create-warm-home-environment',
    title: '打造温馨家庭氛围：从空间布置到情感温度',
    category: '家庭经营之道',
    categorySlug: 'family-management',
    excerpt: '一个温暖的家，不在于面积大小和装修多贵，而在于每个细节都透着用心和爱。分享打造温馨家庭的实用心得。',
    content: `<p>家的温度，不取决于暖气开到多少度，也不取决于房子有多大。它取决于你在冰箱上贴的那张便利贴，取决于餐桌上那束每周一换的鲜花，取决于沙发上那条柔软的毯子。真正的温馨，藏在每一个用心的细节里。</p>
<h2>空间布置：用细节说话</h2>
<p>新婚家庭的布置，是两个人审美和习惯的第一次融合。重点不在于追求多豪华的装修，而在于创造属于"我们"的空间记忆。一起挑选的餐具、一起挂上的照片墙、一起栽种的那盆绿植——这些共同参与的过程，比结果本身更有意义。</p>
<p>建议从卧室和客厅开始。卧室是你们最私密的空间，选择让两个人都感到舒适放松的色调和材质。客厅是接待亲友和共度时光的地方，多一些温暖的元素：一盏暖光的落地灯、几个色彩柔和的靠垫、一本随手可翻的相册。</p>
<h2>日常仪式：家是最好的港湾</h2>
<p>仪式感不是奢侈，而是对生活的尊重。每周一次的家庭电影夜，每月一次的"烛光晚餐"（哪怕只是外卖），每季度一次的家庭大扫除日——这些固定的仪式会让家从"住的地方"升级为"归属的地方"。</p>
<p>特别推荐新婚夫妻建立一个"感恩罐"：每当对方做了一件让你感动的事，写下来放进罐子里。到了年底一起打开，你会发现这一年收获了满满的温暖。</p>
<h2>情感温度：家里最重要的"装修"</h2>
<p>一个家的核心，永远是住在里面的人。无论空间多精致，如果里面的人冷言冷语，那么这就是一个冰冷的房子。反过来，即使住在租来的小公寓里，如果两个人彼此温暖、互相体谅，那这就是一个热气腾腾的家。</p>
<p>每天至少做一件让对方感到被在乎的小事。可以是他加班时的一杯热茶，可以是你疲惫时他主动承包的洗碗任务。家的温暖，就是在这些不起眼的瞬间一点一滴积攒起来的。</p>`,
    imageSeq: 'article-family-1',
    date: '2026-07-18',
    readTime: '8分钟',
    author: '心安',
    tags: ['家庭经营', '家居生活', '仪式感'],
    featured: false,
  },
  {
    id: '6',
    slug: 'self-growth-in-marriage',
    title: '在婚姻中保持自我：已婚女性的成长必修课',
    category: '自我成长',
    categorySlug: 'self-growth',
    excerpt: '婚姻不是自我的终点，而是一个人变得更完整的旅程。学会在婚姻中持续成长，做那个依然闪闪发光的自己。',
    content: `<p>"结婚后，我感觉自己变了。"很多新婚女性会在婚后一两年产生这样的感慨。她们发现自己的生活重心完全转移到了丈夫和家庭上，曾经热爱的兴趣被搁置，曾经坚持的习惯被打破，那个活力四射的女孩似乎越走越远。</p>
<h2>为什么在婚姻中保持自我如此重要？</h2>
<p>一个令人遗憾的事实是：完全牺牲自我去成全婚姻的女人，往往最终也留不住好的婚姻。因为当初吸引丈夫的，正是那个独立、有趣、有光芒的你。当你变成一个只围着家庭转的"附属品"时，你不仅失去了自己，也失去了婚姻中最宝贵的吸引力。</p>
<p>保持自我不是对婚姻的不忠，恰恰相反，它是让婚姻长期保鲜的秘诀。一个有自己世界的人，才能给伴侣带来持续的惊喜和新鲜感。</p>
<h2>四个在婚姻中持续成长的方法</h2>
<p><strong>守护你的独处时间。</strong>每周至少留出两个小时完全属于你自己的时间。可以是阅读、运动、写作、画画——任何让你感到充电和快乐的事情。这不是自私，而是必要的自我滋养。</p>
<p><strong>保持职业成长。</strong>无论你是全职工作还是全职主妇，都不要停止在某个领域的深入学习。职业能力是你独立生活的基础，也是你自信的来源。</p>
<p><strong>维护你的社交圈。</strong>不要因为结婚就切断和朋友的联系。你的闺蜜、你的同事、你的兴趣小组——这些人际关系是你情感支持系统的重要组成部分。</p>
<p><strong>和伴侣一起成长。</strong>最好的婚姻状态是夫妻互为成长的催化剂。鼓励对方追求梦想，也接受对方的鼓励和支持。你们可以是最亲密的战友，在各自的人生战场上互相加油。</p>`,
    imageSeq: 'article-self-1',
    date: '2026-07-15',
    readTime: '9分钟',
    author: '心安',
    tags: ['自我成长', '女性独立', '婚姻保鲜'],
    featured: true,
  },
  {
    id: '7',
    slug: 'how-to-handle-in-law-relationship',
    title: '婆媳关系的正确打开方式：新婚妻子的智慧手册',
    category: '婚后角色转变',
    categorySlug: 'role-transition',
    excerpt: '婆媳关系是许多新婚女性最焦虑的话题之一。其实不必过度紧张，掌握这些原则，你也能轻松经营好这段特殊的关系。',
    content: `<p>在中国文化背景下，婆媳关系几乎是每对新婚夫妻绕不开的课题。很多女性在婚前就已经开始焦虑：婆婆会喜欢我吗？我能处理好和她的关系吗？如果发生矛盾怎么办？这些担忧非常正常，但你完全不必被它们困住。</p>
<h2>重新理解婆媳关系的本质</h2>
<p>婆媳关系的特殊性在于：两个原本陌生的女人，因为爱着同一个男人而成为家人。这本身就决定了它不同于母女关系——母女之间有无条件的血缘纽带作为缓冲，而婆媳之间需要时间和智慧来建立信任和感情。</p>
<p>理解这一点非常重要：你和婆婆不需要成为亲密无间的闺蜜，只需要成为互相尊重的家人。降低不切实际的期待，反而会让关系更加轻松。</p>
<h2>三个关键原则</h2>
<p><strong>尊重先行。</strong>无论婆婆的处事方式和你有多大的差异，她是丈夫的母亲，是长辈。尊重的姿态不是示弱，而是智慧。尊重她的经验、她的付出以及她在家庭中的位置。</p>
<p><strong>丈夫是桥梁，不是裁判。</strong>婆媳关系的好坏，很大程度上取决于丈夫（儿子）的角色。你需要和丈夫达成共识：他是你们之间的沟通桥梁，而不是选边站的裁判。提前和丈夫聊一聊彼此在婆媳关系中的角色期待，会避免很多日后的误会。</p>
<p><strong>保持恰当的亲密距离。</strong>太远了显得冷漠，太近了容易摩擦。找到那个不远不近的"刚刚好"，是婆媳关系的最高境界。可以从每周一次视频通话、每月一次家庭聚餐开始，根据双方的舒适度慢慢调整。</p>
<h2>化解矛盾的正确姿势</h2>
<p>当分歧出现时，不要急于辩对错。先听，再想，最后温和地表达。用"我理解您的想法，同时我也有一些不同的考虑"这样的句式，而不是"您说得不对"。记住，赢了道理输了关系，在家庭中是最不划算的交易。</p>`,
    imageSeq: 'article-role-2',
    date: '2026-07-12',
    readTime: '11分钟',
    author: '心安',
    tags: ['婆媳关系', '家庭关系', '角色转变'],
    featured: false,
  },
  {
    id: '8',
    slug: 'love-language-for-couples',
    title: '爱的五种语言：找到你和他的情感密码',
    category: '夫妻相处技巧',
    categorySlug: 'couple-skills',
    excerpt: '为什么你精心准备的礼物他不为所动，而你最想要的只是一个拥抱？了解彼此的爱之语言，让付出和接收都恰到好处。',
    content: `<p>你有没有过这样的困惑：你花了一整个下午做了一桌他爱吃的菜，他吃完说了句"好吃"就去打游戏了，你觉得自己的付出没有得到应有的回应。而另一次，他只是在你加班时发了一条"注意休息"的消息，你却感动了很久。</p>
<p>这不是谁对谁错的问题，而是你们说着不同的"爱的语言"。</p>
<h2>爱的五种语言详解</h2>
<p>美国婚姻咨询师盖瑞·查普曼提出，人们表达和接收爱的方式主要有五种：</p>
<p><strong>肯定的言语：</strong>对你来说，一句真诚的赞美比任何礼物都珍贵。"你今天真好看"、"你做的决定很正确"——这些肯定的话语是你最需要的爱的证明。</p>
<p><strong>精心的时刻：</strong>你需要的是全神贯注的陪伴。不是坐在同一张沙发上各玩各的手机，而是放下一切，看着对方的眼睛，进行一次真正认真的对话或共同完成一件事。</p>
<p><strong>接受礼物：</strong>礼物对你来说不只是物品，而是"他想着我"的证据。礼物不一定要贵，但一定要用心。一小束路边买的花、一本你提过的书，都能让你感受到被爱。</p>
<p><strong>服务的行动：</strong>他认为"帮你做点什么"就是爱。主动洗碗、帮你拿快递、在你累的时候按按肩膀——这些实际的行动就是他的情话。</p>
<p><strong>身体的接触：</strong>牵手、拥抱、亲吻、亲密接触——对你来说，身体的触碰是最直接的情感连接。你无法从语言或礼物中获得同样的安全感。</p>
<h2>如何找到彼此的爱之语言</h2>
<p>最简单的方法是各自回答这个问题："对方做什么事的时候，你最能感受到被爱？"以及"当你想表达爱的时候，你通常会做什么？"前者揭示你的接收语言，后者揭示你的表达语言。夫妻俩坐下来做这个练习，你们会对彼此有全新的认识。</p>`,
    imageSeq: 'article-couple-2',
    date: '2026-07-10',
    readTime: '9分钟',
    author: '心安',
    tags: ['爱的语言', '夫妻沟通', '情感表达'],
    featured: false,
  },
  {
    id: '9',
    slug: 'marriage-financial-planning-guide',
    title: '新婚家庭理财入门：从零开始的财务规划指南',
    category: '婚姻经营秘诀',
    categorySlug: 'marriage-secrets',
    excerpt: '钱不是婚姻的全部，但财务问题是许多婚姻矛盾的重要来源。新婚期就是建立健康财务习惯的黄金窗口。',
    content: `<p>在离婚原因排行榜上，财务问题始终稳居前三。不是因为钱本身有多重要，而是因为金钱观的分歧会延伸到价值观、信任感和权力分配等更深层的问题上。新婚期，是两个独立的财务系统第一次融合的时期，这个时候打下好的基础，能避免未来大量的争吵和误解。</p>
<h2>第一步：坦诚的财务"坦白局"</h2>
<p>找一个轻松的时间，两个人坐下来，坦诚地分享各自的财务状况。包括收入、存款、负债（信用卡、贷款等）、每月固定支出以及各自的消费习惯。这不是查账，而是建立信任。很多夫妻结婚多年都不知道对方的真实收入或负债，这种信息不对称是定时炸蛋。</p>
<p>如果你们之前没有聊过这个话题，可以从"你觉得我们以后钱应该怎么管"这个开放式问题开始。听对方的想法，而不是急着给出你的方案。</p>
<h2>第二步：确定财务管理模式</h2>
<p>常见的家庭财务管理有三种模式：完全合并、完全独立、混合模式。没有哪种是最优的，最适合你们的就是最好的。</p>
<p>对于大多数新婚夫妻，推荐从混合模式开始：设立一个共同账户用于日常开销和共同储蓄，各自保留一部分个人资金用于个人消费。这样既保证了家庭的统一性，又保留了各自的独立空间。</p>
<h2>第三步：制定共同的财务目标</h2>
<p>你们想在几年内买房？计划什么时候要孩子？每年至少旅行一次？把这些共同目标写下来，然后倒推需要每个月存多少钱。当两个人为同一个目标努力时，存钱不再是痛苦的克制，而是充满期待的积累。</p>
<p>建议设立三个账户：应急基金（3-6个月的生活费）、短期目标基金（1-3年内的大额支出）、长期投资账户。</p>`,
    imageSeq: 'article-marriage-2',
    date: '2026-07-08',
    readTime: '10分钟',
    author: '心安',
    tags: ['家庭理财', '财务规划', '新婚经济'],
    featured: false,
  },
  {
    id: '10',
    slug: 'daily-routine-as-newlyweds',
    title: '新婚夫妻的日常节奏：建立让你们都舒服的生活秩序',
    category: '新婚适应期攻略',
    categorySlug: 'newlywed-guide',
    excerpt: '两个人从各自独立的生活节奏，到共享同一个生活空间，日常习惯的磨合是必不可少的过程。这里有一份和谐共处的实用方案。',
    content: `<p>谈恋爱时，你们的日程安排是独立的。约会的时间是特意腾出来的，其余时间各过各的生活。结婚后，你们要在同一个空间里共生共存，日常习惯的差异立刻浮出水面。他习惯早起，你喜欢赖床；他喜欢边吃饭边看手机，你觉得吃饭就该专心聊天。这些看似不起眼的差异，累积起来可能比一次大吵更消磨感情。</p>
<h2>认识到差异不是问题</h2>
<p>首先要建立正确的认知：习惯差异是正常的。你们来自两个不同的家庭，有着不同的成长背景，有不同的生活习惯是理所当然的事。不要把自己的习惯当成"对的"，把对方的习惯当成"需要纠正的"。</p>
<p>每一对新婚夫妻都需要一个"习惯校准"的过程。这个过程不是谁改变谁，而是两个人在彼此的习惯之间找到一个双方都能接受的平衡点。</p>
<h2>建立属于你们的日常节奏</h2>
<p>从周一到周五的工作日节奏开始规划。几点起床、谁负责早餐、晚上几点是"共同时光"、几点是"各自自由时间"——把这些最基本的日常节点做一个大概的安排。</p>
<p>周末则可以有更多的弹性。可以约定：周六上午是各自的时间，周六下午和周日是共同活动时间。这样的安排既满足了各自的独处需求，又保证了足够的陪伴质量。</p>
<h2>家务分工的艺术</h2>
<p>家务是大部分新婚夫妻的第一个"战场"。建议不要在"谁做得多谁做得少"上纠结，而是根据各自擅长和愿意做的来分工。他擅长做饭就负责厨房，你擅长整理就负责收纳。都不擅长的，可以商量外包——请钟点工不是一个坏主意，花一点钱换来家庭和谐，这笔投资太划算了。</p>`,
    imageSeq: 'article-newlywed-2',
    date: '2026-07-05',
    readTime: '8分钟',
    author: '心安',
    tags: ['日常习惯', '生活节奏', '家务分工'],
    featured: false,
  },
  {
    id: '11',
    slug: 'meal-planning-for-new-family',
    title: '新婚小家庭的餐桌哲学：从三餐到情感的温暖连接',
    category: '家庭经营之道',
    categorySlug: 'family-management',
    excerpt: '厨房是一个家的心脏，餐桌是情感交流最温暖的地方。为新婚家庭量身定制的餐桌解决方案，让每一餐都充满爱。',
    content: `<p>有一种说法：判断一个家庭的幸福指数，去看他们的餐桌就知道了。餐桌上是否有说有笑、是否摆满了用心烹饪的食物、是否有足够的时间慢慢享用——这些细节比任何问卷调查都更真实地反映着一个家的温度。</p>
<h2>餐桌不只是吃饭的地方</h2>
<p>在新婚家庭里，餐桌承担着多重角色。它是每天下班后两个人第一个真正面对面交流的地方，是周末招待朋友的社交中心，也是未来孩子们建立家庭记忆的重要场景。把餐桌经营好，等于把家庭的情感中枢经营好。</p>
<p>给餐桌一些仪式感：铺上你们一起挑选的桌布，放一小瓶鲜花（哪怕只是阳台上的绿萝），点上那盏暖黄色的吊灯。这些细节不需要花很多钱，但它们告诉彼此：这一餐很重要，我们的相处很重要。</p>
<h2>新手厨房的入门指南</h2>
<p>如果你们都是厨房新手，不用担心。现在的厨房工具和食谱资源非常丰富，从零开始学做饭并不是一件难事。关键是要有"我们一起学"的心态——把做饭当成两个人的共同爱好，而不是某个人的责任。</p>
<p>建议从每周规划3-4顿在家吃的晚餐开始。周末花半小时做好一周的菜单和采购清单，工作日就不会手忙脚乱。准备几个"十分钟快手菜"作为救急方案——冰箱里常备鸡蛋、番茄、速冻蔬菜和面条，任何时候都能快速变出一顿像样的饭。</p>
<h2>周末的厨房时光</h2>
<p>工作日的晚餐可以追求效率和营养，周末的厨房则可以变成你们的游乐场。一起尝试一道新菜、一起烘焙一次饼干、或者一起包一顿饺子——这些需要合作和等待的烹饪活动，是最好的夫妻团建。在等着面团发酵的时候聊天，在包饺子的过程中比赛谁包得更好看，在这些看似普通的时刻，感情正在悄然升温。</p>`,
    imageSeq: 'article-family-2',
    date: '2026-07-03',
    readTime: '9分钟',
    author: '心安',
    tags: ['餐桌文化', '家庭烹饪', '日常温情'],
    featured: false,
  },
  {
    id: '12',
    slug: 'maintain-personal-hobbies',
    title: '婚后如何坚持你的个人爱好：给兴趣留一个位置',
    category: '自我成长',
    categorySlug: 'self-growth',
    excerpt: '很多女性在婚后不知不觉放弃了自己的爱好。本文告诉你为什么这很危险，以及如何优雅地在婚姻中守护你的热爱。',
    content: `<p>"我以前每周都去画室，结婚后就再也没去过了。" "我以前很喜欢周末去爬山，现在周末都用来陪老公了。" 这样的声音在新婚女性中太常见了。似乎结婚就意味着要把自己的兴趣爱好收起来，放进一个叫做"过去"的箱子里。</p>
<p>但事实是：放弃爱好不是为婚姻做出的"牺牲"，而是对自己人生的一种"亏欠"。</p>
<h2>爱好为什么如此重要？</h2>
<p>爱好不仅仅是一种消遣。它是你身份认同的重要部分，是你减压和充电的方式，是你社交圈的连接纽带，也是你在妻子和女儿之外作为独立个体存在的重要证明。当你放弃爱好时，你放弃的不只是一项活动，而是自己的一部分。</p>
<p>而且讽刺的是，一个放弃了自己兴趣爱好的女人，在婚姻中往往魅力大减。那个充满热情、有自己世界的女人，才是最有吸引力的伴侣。</p>
<h2>五个方法守护你的爱好</h2>
<p><strong>固定时间法：</strong>把爱好写进日程表，像对待工作会议一样认真对待。每周三晚上是瑜伽时间，周六上午是画画时间——固定下来，就不容易被临时的事挤掉。</p>
<p><strong>迷你版法：</strong>如果你以前每周要花一整天在爱好上，现在可以缩小到每次一两个小时。时间虽然少了，但持续去做比完全放弃要强一百倍。</p>
<p><strong>伴侣参与法：</strong>邀请丈夫参与你的爱好，或者至少让他了解和尊重你的爱好。你可以带他去看一次你的画展，或者和他分享你最近拍的照片。让他看到你在爱好中发光的样子。</p>
<p><strong>社交绑定法：</strong>把爱好和社交结合起来。和朋友一起报瑜伽课、和闺蜜约好一起去徒步——这样你就不会因为"不想一个人去"而放弃。</p>
<p><strong>心态调整法：</strong>不要因为不能像单身时那样投入就感到沮丧。接受"现在的时间有限，但我依然在坚持"这样一个状态。完美主义是爱好最大的敌人。</p>`,
    imageSeq: 'article-self-2',
    date: '2026-06-30',
    readTime: '9分钟',
    author: '心安',
    tags: ['个人爱好', '自我坚持', '生活平衡'],
    featured: false,
  },
  {
    id: '13',
    slug: 'balancing-career-and-family',
    title: '职场与家庭的天平：新婚职业女性的平衡之道',
    category: '婚后角色转变',
    categorySlug: 'role-transition',
    excerpt: '结婚后如何平衡事业和家庭，是许多职场女性的核心焦虑。答案不是二选一，而是找到属于你的节奏。',
    content: `<p>新婚的甜蜜期过后，一个现实的问题很快浮出水面：如何在妻子和职场人两个角色之间找到平衡？尤其是在中国传统观念依然强调"女主内"的背景下，新婚职业女性面临着比男性更复杂的角色期待。</p>
<h2>打破"完美平衡"的迷思</h2>
<p>首先需要清醒认识到：绝对的平衡是不存在的。你不会在同一天既完成一个重要的工作项目，又把家里收拾得一尘不染，还做了一顿营养丰盛的晚餐。追求这种不可能的标准，只会让自己精疲力竭。</p>
<p>真正可行的不是每天的平衡，而是长期的动态平衡。有些阶段工作会更忙，有些阶段家庭需要更多投入。接受这种波动，而不是在每一个时刻都要求面面俱到。</p>
<h2>和丈夫达成"双职家庭"的共识</h2>
<p>这是非常关键的一步。你需要和丈夫明确沟通：你们的家庭是"双职家庭"，而不是"他主外你主内"的传统模式。这意味着家务、育儿（如果有的话）和家庭事务是两个人的共同责任。</p>
<p>这种共识需要在日常细节中体现。不是你去"帮"他做家务，也不是他来"帮"你带孩子——这些都是你们共同的分内之事。建议做一个"家庭责任清单"，把所有的家务和家庭事务列出来，然后根据各自的擅长和时间来分配。</p>
<h2>善用工具和外援</h2>
<p>聪明的主妇不是什么事都亲力亲为的人，而是善于调动资源的人。家用电器（洗碗机、扫地机器人）、外卖服务、钟点工——这些都是现代双职家庭的好帮手。不要把花钱请外援看成是懒惰或浪费，这是用金钱购买时间和精力，是效率最高的投资。</p>`,
    imageSeq: 'article-role-3',
    date: '2026-06-28',
    readTime: '10分钟',
    author: '心安',
    tags: ['职场平衡', '角色转变', '双职家庭'],
    featured: false,
  },
  {
    id: '14',
    slug: 'conflict-resolution-for-couples',
    title: '吵架的艺术：新婚夫妻如何把冲突变成感情的催化剂',
    category: '夫妻相处技巧',
    categorySlug: 'couple-skills',
    excerpt: '每一对夫妻都会吵架，区别在于有人越吵越亲密，有人越吵越疏远。学会"建设性吵架"，让分歧成为你们感情的加油站。',
    content: `<p>如果你觉得"好的婚姻从不吵架"，那你要失望了。事实上所有幸福的婚姻都有争吵，真正区分幸福婚姻和不幸婚姻的，不是吵不吵架，而是怎么吵架。</p>
<h2>认清吵架的本质</h2>
<p>大部分夫妻争吵的导火索都是鸡毛蒜皮的小事——马桶盖没放下、衣服乱扔、忘记买什么东西。但如果你深入挖掘，会发现这些小事的背后往往隐藏着更深层次的需求：被尊重的需求、被在乎的需求、被理解的需求。</p>
<p>所以当你们为"你又忘记倒垃圾"争吵时，真正的问题可能不是垃圾，而是"我觉得你不在乎我说的话"。学会在争吵中识别表面的"导火索"和深层的"真正需求"，是建设性吵架的第一步。</p>
<h2>建设性吵架的五个规则</h2>
<p><strong>规则一：不翻旧账。</strong>就事论事，这次的问题就是这次的问题。把三个月前的旧事翻出来，只会让争吵升级为对彼此人格的攻击。</p>
<p><strong>规则二：不用"你总是"和"你从不"。</strong>这些绝对化的表述不仅不准确，而且极具攻击性。换成"这次你..."或"最近我注意到..."。</p>
<p><strong>规则三：不涉及对方家庭。</strong>无论多生气，"你妈说..."、"你家人..."都是禁忌话题。家庭是每个人的情感底线，触碰底线会让争吵的性质完全改变。</p>
<p><strong>规则四：允许暂停。</strong>如果情绪已经失控，可以说"我现在情绪不太好，能不能暂停半小时我们再聊"。暂停不是逃避，而是为了更理性地回来解决问题。</p>
<p><strong>规则五：吵完要和好。</strong>争吵之后的修复比争吵本身更重要。一个主动的拥抱、一句真诚的道歉、一次认真的复盘——让每次争吵都以"我们更了解彼此了"结束。</p>`,
    imageSeq: 'article-couple-3',
    date: '2026-06-25',
    readTime: '10分钟',
    author: '心安',
    tags: ['夫妻吵架', '冲突解决', '情感修复'],
    featured: false,
  },
  {
    id: '15',
    slug: 'keep-romance-alive-in-marriage',
    title: '婚姻中的浪漫保鲜术：让爱情在柴米油盐中持续发酵',
    category: '婚姻经营秘诀',
    categorySlug: 'marriage-secrets',
    excerpt: '很多夫妻说"结婚后浪漫就消失了"。其实浪漫没有消失，只是换了一种形式。学会发现和创造婚姻中的浪漫，让每一天都像恋爱。',
    content: `<p>"他以前追我的时候每天都会发早安晚安，现在连微信都懒得回。" "以前约会他会提前半小时到，现在出门永远是我在催他。" 这些抱怨你一定不陌生。似乎婚姻天然是浪漫的坟墓——但你相信吗，那些结婚十几年依然甜蜜的夫妻，他们的秘诀不是"回到过去"，而是"创造现在"。</p>
<h2>重新定义婚姻中的浪漫</h2>
<p>恋爱时的浪漫往往是"表演型"的——精心安排的约会、刻意准备的惊喜、努力展示最好的一面。而婚姻中的浪漫更像是一种"渗透型"的——它散落在日常的缝隙里，不张扬但更持久。</p>
<p>他在你加班时默默热好的晚饭，是浪漫。她记住你无意间提到想买的那本书然后偷偷下单，是浪漫。早上起来发现对方已经帮你挤好了牙膏，也是浪漫。婚姻的浪漫不在于形式多华丽，而在于那份"我一直把你放在心上"的持续关注。</p>
<h2>六个让婚姻保持新鲜感的方法</h2>
<p><strong>定期约会：</strong>即使结了婚，也要保持"约会"的习惯。每周或每两周一次正式的二人时光——穿得漂漂亮亮地出去吃顿饭，或者去看一场电影。不是为了做什么特别的事，而是为了保持"我们依然在谈恋爱"的感觉。</p>
<p><strong>制造小惊喜：</strong>不需要等到生日或纪念日。路过花店时带一枝花回家，在冰箱上贴一张写有"今天也很喜欢你"的便利贴，偷偷在他的包里放一包他喜欢的零食。小惊喜的成本极低，但效果惊人。</p>
<p><strong>共同创造新鲜体验：</strong>一起尝试从未做过的事情——学一门新技能、去一个没去过的地方、看一场从来没看过的演出。新鲜感是浪漫最强大的燃料。</p>
<p><strong>保持身体亲密：</strong>不止是性生活，每天的拥抱、牵手、亲吻额头——这些非性的身体接触会持续释放让人感到亲密和幸福的荷尔蒙。</p>`,
    imageSeq: 'article-marriage-3',
    date: '2026-06-22',
    readTime: '9分钟',
    author: '心安',
    tags: ['浪漫保鲜', '婚姻仪式', '爱情维持'],
    featured: false,
  },
  {
    id: '16',
    slug: 'mental-health-for-newlyweds',
    title: '新婚期的情绪管理：当焦虑来袭时如何自我调节',
    category: '新婚适应期攻略',
    categorySlug: 'newlywed-guide',
    excerpt: '新婚的快乐是真的，偶尔的焦虑和不安也是真的。了解新婚期常见的情绪波动，学会温柔地照顾自己的心理健康。',
    content: `<p>社会对婚姻的描绘往往是非黑即白的：要么是童话般的"从此幸福地生活在一起"，要么是悲观的"婚姻是爱情的坟墓"。很少有人告诉你真相：新婚期是一个既幸福又充满挑战的过渡阶段，你完全可能在爱着丈夫的同时，偶尔感到焦虑、不安甚至失落。</p>
<h2>认识新婚期的常见情绪</h2>
<p><strong>"我是不是做了错误的决定？"</strong>——当你们第一次大吵一架之后，这个念头可能会闪过你的脑海。这是正常的！重大人生决定之后出现短暂的怀疑，是大脑的自我保护机制在运作，不代表你真的选错了人。</p>
<p><strong>"我好像没有以前快乐了？"</strong>——单身时的快乐来源很多元：朋友聚会、独自旅行、shopping的即时满足。婚后你的注意力更集中在一个人和一段关系上，快乐的来源变得集中了。这不是快乐变少了，而是快乐的形式变化了。</p>
<p><strong>"我们的生活太平淡了？"</strong>——蜜月期的激情退去后，日常的平淡感可能会让你不安。但平淡不等于不幸，平稳是婚姻最健康的状态。学会在平淡中发现幸福，是婚姻中最珍贵的能力。</p>
<h2>情绪自救工具包</h2>
<p><strong>情绪日记：</strong>每天花五分钟记录自己的情绪状态。不需要长篇大论，几个关键词就够了。一周后回头看看，你会发现大多数负面情绪都是暂时的、有具体原因的——这个认知本身就有治愈作用。</p>
<p><strong>运动处方：</strong>当焦虑来袭时，先不要急着和丈夫"谈谈"，去跑步、跳操、做瑜伽。用身体的活动来释放心理的压力，往往比语言沟通更有效。</p>
<p><strong>社交支持：</strong>保持和已婚闺蜜的联系。有一个可以倾诉、可以对比、可以一起吐槽的对象，是情绪安全网的重要组成部分。你会发现你正在经历的，她们也经历过。</p>`,
    imageSeq: 'article-newlywed-3',
    date: '2026-06-20',
    readTime: '10分钟',
    author: '心安',
    tags: ['情绪管理', '心理健康', '新婚焦虑'],
    featured: false,
  },
];

export const testimonials: Testimonial[] = [
  {
    id: 't1',
    name: '小雨',
    role: '新婚6个月',
    content: '刚结婚的时候真的很迷茫，不知道该怎么做一个妻子。看了这个网站的文章后，发现原来很多人都有同样的困惑，一下子觉得不孤单了。现在每次遇到问题都会先来这里找找思路，真的帮了我很多。',
    avatar: 'ri-user-3-line',
  },
  {
    id: 't2',
    name: '静怡',
    role: '结婚2年',
    content: '最打动我的是文章里的那种温度，不是高高在上的说教，而是像闺蜜一样温柔地和你分享。特别是关于婆媳关系那篇，看完之后我和老公认真聊了一次，现在我们家的氛围好多了。',
    avatar: 'ri-user-3-line',
  },
  {
    id: 't3',
    name: '文婷',
    role: '新婚3个月',
    content: '作为一个职场女性，婚后的焦虑感一度让我崩溃。这里的文章让我明白平衡不是每天的功课，而是长期的智慧。现在学会了给自己松绑，反而和老公的关系更好了。',
    avatar: 'ri-user-3-line',
  },
  {
    id: 't4',
    name: '美玲',
    role: '结婚1年半',
    content: '把这里推荐给了好几个刚结婚的姐妹，大家都说太实用了！最喜欢"家庭经营之道"这个栏目，每次看完都觉得自己又学到了新东西。希望这个网站一直做下去！',
    avatar: 'ri-user-3-line',
  },
];

export const faqs = [
  {
    question: '第一次的人妻网站是关于什么的？',
    answer: '我们是一个专注于为新婚女性提供实用生活指南的网站。内容涵盖婚后角色转变、夫妻相处技巧、婚姻经营秘诀、新婚适应期攻略、家庭经营之道以及自我成长等多个主题，帮助每一位初为人妻的女性从容迎接幸福新生活。',
  },
  {
    question: '网站内容多久更新一次？',
    answer: '我们每周更新2-3篇原创文章，确保内容始终保持新鲜和实用。你可以通过首页的邮件订阅功能第一时间获取最新内容。',
  },
  {
    question: '如何选择适合自己的文章阅读？',
    answer: '我们按照不同的主题分类了所有文章，你可以在首页的分类导航中找到自己感兴趣的主题，也可以使用搜索功能查找特定话题。如果你是新婚不久，建议从"新婚适应期攻略"分类开始阅读。',
  },
  {
    question: '可以分享自己的新婚经验和故事吗？',
    answer: '当然可以！我们非常欢迎读者分享自己的真实经历和感悟。你可以通过"关于我们"页面的联系表单与我们取得联系，你的故事可能会帮助到更多正在经历同样阶段的新婚女性。',
  },
  {
    question: '这些建议适用于所有类型的新婚夫妻吗？',
    answer: '每对夫妻的情况都是独特的，我们所提供的建议是基于普遍经验和专业知识的参考。我们鼓励读者根据自己的实际情况灵活运用，并始终保持与伴侣的开放沟通。婚姻没有标准答案，最重要的是找到适合你们的方式。',
  },
];